import React, { useEffect, useState, useRef } from 'react';
import { useOutletContext, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import api from '../../api/axios';
import { FiShoppingCart, FiPlus, FiTrash2, FiCopy, FiLink, FiX, FiRotateCcw, FiSlash, FiTruck } from 'react-icons/fi';
import { dt, DashLang } from '../../utils/dashboardTranslations';

const STORAGE_URL = (import.meta as any).env?.VITE_STORAGE_URL || 'https://cartz.cc/storage';
const statuses = ['pending', 'confirmed', 'preparing', 'ready', 'out_for_delivery', 'delivered', 'cancelled'];

export default function Orders() {
  const { store, lang, myPerms } = useOutletContext<{ store: any; lang: DashLang; myPerms: any }>();
  const navigate = useNavigate();
  const [orders, setOrders] = useState<any[]>([]);
  const [selectedOrder, setSelectedOrder] = useState<any>(null);
  const [filterStatus, setFilterStatus] = useState('');
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);

  // Create order modal
  const [showCreate, setShowCreate] = useState(false);
  const [products, setProducts] = useState<any[]>([]);
  const [productSearch, setProductSearch] = useState('');
  const [orderItems, setOrderItems] = useState<{ product: any; quantity: number }[]>([]);
  const [customerName, setCustomerName] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const [customerEmail, setCustomerEmail] = useState('');
  const [orderNotes, setOrderNotes] = useState('');
  const [creating, setCreating] = useState(false);
  const [customerMode, setCustomerMode] = useState<'existing' | 'new'>('existing');
  const [customerSearch, setCustomerSearch] = useState('');
  const [customers, setCustomers] = useState<any[]>([]);
  const [selectedCustomer, setSelectedCustomer] = useState<any>(null);
  const [customerSearchTimer, setCustomerSearchTimer] = useState<any>(null);

  // Payment link result
  const [paymentLink, setPaymentLink] = useState('');
  const [showLinkModal, setShowLinkModal] = useState(false);
  const isOwner = myPerms?.is_owner === true;
  const orderPerms = myPerms?.permissions?.orders || {};
  const can = (action: string) => isOwner || !!orderPerms[action];

  useEffect(() => {
    if (store?.id) loadOrders();
  }, [store?.id, filterStatus, page]);

  const loadOrders = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterStatus) params.set('status', filterStatus);
      params.set('page', String(page));
      const res = await api.get(`/stores/${store.id}/orders?${params}`);
      const data = res.data;
      if (page === 1) {
        setOrders(data.data || []);
      } else {
        setOrders((prev) => [...prev, ...(data.data || [])]);
      }
      setLastPage(data.last_page || 1);
    } finally { setLoading(false); }
  };

  const updateStatus = async (orderId: number, status: string) => {
    if (status === 'cancelled') {
      const order = orders.find((item) => item.id === orderId);
      if (order) await runOrderAction(order, 'cancel');
      return;
    }
    try {
      const res = await api.put(`/stores/${store.id}/orders/${orderId}/status`, { status });
      toast.success(dt('order_status_updated', lang));

      // The endpoint answers {order, riders} when an auto-dispatch was tried.
      const updated = res.data?.order || res.data;
      const riders = res.data?.riders;
      if (riders) {
        riders.ok ? toast.success(riders.message) : toast.warn(riders.message, { autoClose: 8000 });
      }

      setOrders((prev) => prev.map((o) => o.id === orderId ? { ...o, ...updated, status } : o));
      if (selectedOrder?.id === orderId) setSelectedOrder({ ...selectedOrder, ...updated, status });
    } catch {
      toast.error(dt('error_updating_status', lang));
    }
  };

  const runOrderAction = async (order: any, action: 'cancel' | 'refund') => {
    const promptText = action === 'refund'
      ? (lang === 'ar' ? 'أدخل سبب استرجاع المبلغ:' : 'Enter the refund reason:')
      : (lang === 'ar' ? 'أدخل سبب إلغاء الطلب:' : 'Enter the cancellation reason:');
    const reason = window.prompt(promptText)?.trim();
    if (!reason) return;

    try {
      const res = await api.post(`/stores/${store.id}/orders/${order.id}/${action}`, { reason });
      const updated = res.data;
      setOrders((prev) => prev.map((item) => item.id === order.id ? { ...item, ...updated } : item));
      if (selectedOrder?.id === order.id) setSelectedOrder({ ...selectedOrder, ...updated });
      toast.success(action === 'refund'
        ? (lang === 'ar' ? 'تم تسجيل الاسترجاع وإعادة المخزون' : 'Refund recorded and stock restored')
        : (lang === 'ar' ? 'تم إلغاء الطلب وإعادة المخزون' : 'Order cancelled and stock restored'));
    } catch (err: any) {
      toast.error(err.response?.data?.message || (lang === 'ar' ? 'تعذر تنفيذ العملية' : 'Unable to complete this action'));
    }
  };

  const loadProducts = async () => {
    try {
      const res = await api.get(`/stores/${store.id}/products?per_page=999`);
      setProducts(res.data.data || res.data || []);
    } catch {}
  };

  const openCreateModal = () => {
    setShowCreate(true);
    setOrderItems([]);
    setCustomerName('');
    setCustomerPhone('');
    setCustomerEmail('');
    setOrderNotes('');
    setProductSearch('');
    setCustomerMode('existing');
    setCustomerSearch('');
    setCustomers([]);
    setSelectedCustomer(null);
    loadProducts();
  };

  const searchCustomers = async (q: string) => {
    if (!q || q.length < 2) { setCustomers([]); return; }
    try {
      const res = await api.get(`/stores/${store.id}/customers?search=${encodeURIComponent(q)}`);
      setCustomers(res.data?.data || res.data || []);
    } catch { setCustomers([]); }
  };

  const handleCustomerSearchChange = (val: string) => {
    setCustomerSearch(val);
    setSelectedCustomer(null);
    if (customerSearchTimer) clearTimeout(customerSearchTimer);
    setCustomerSearchTimer(setTimeout(() => searchCustomers(val), 300));
  };

  const selectCustomer = (c: any) => {
    setSelectedCustomer(c);
    setCustomerName(c.name || '');
    setCustomerPhone(c.phone || '');
    setCustomerEmail(c.email || '');
    setCustomerSearch(c.name + (c.phone ? ` - ${c.phone}` : ''));
    setCustomers([]);
  };

  const addItem = (product: any) => {
    const existing = orderItems.find((i) => i.product.id === product.id);
    if (existing) {
      setOrderItems(orderItems.map((i) => i.product.id === product.id ? { ...i, quantity: i.quantity + 1 } : i));
    } else {
      setOrderItems([...orderItems, { product, quantity: 1 }]);
    }
    setProductSearch('');
  };

  const removeItem = (productId: number) => {
    setOrderItems(orderItems.filter((i) => i.product.id !== productId));
  };

  const updateQty = (productId: number, qty: number) => {
    if (qty < 1) return;
    setOrderItems(orderItems.map((i) => i.product.id === productId ? { ...i, quantity: qty } : i));
  };

  const orderTotal = orderItems.reduce((sum, i) => sum + (i.product.effective_price || i.product.price) * i.quantity, 0);

  const handleCreateOrder = async () => {
    if (!customerName.trim() || !customerPhone.trim() || orderItems.length === 0) {
      toast.error(lang === 'ar' ? 'يرجى ملء جميع الحقول المطلوبة' : 'Please fill all required fields');
      return;
    }
    setCreating(true);
    try {
      const res = await api.post(`/stores/${store.id}/orders/manual`, {
        customer_name: customerName,
        customer_phone: customerPhone,
        customer_email: customerEmail || undefined,
        notes: orderNotes || undefined,
        items: orderItems.map((i) => ({ product_id: i.product.id, quantity: i.quantity })),
      });
      toast.success(dt('order_created', lang));
      setShowCreate(false);
      setPaymentLink(res.data.payment_link);
      setShowLinkModal(true);
      setPage(1);
      loadOrders();
    } catch (err: any) {
      toast.error(err.response?.data?.message || dt('error_creating_order', lang));
    } finally { setCreating(false); }
  };

  const handleDeleteOrder = async (orderId: number) => {
    if (!window.confirm(lang === 'ar' ? 'هل تريد حذف هذا الطلب؟' : 'Delete this order? This cannot be undone.')) return;
    try {
      await api.delete(`/stores/${store.id}/orders/${orderId}`);
      toast.success(lang === 'ar' ? 'تم حذف الطلب' : 'Order deleted');
      setOrders(prev => prev.filter(o => o.id !== orderId));
      if (selectedOrder?.id === orderId) setSelectedOrder(null);
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Error');
    }
  };

  const copyLink = () => {
    navigator.clipboard.writeText(paymentLink);
    toast.success(dt('link_copied', lang));
  };

  const filteredProducts = products.filter((p) => {
    if (!productSearch) return false;
    const q = productSearch.toLowerCase();
    return p.name?.toLowerCase().includes(q) || p.name_ar?.includes(q) || p.sku?.toLowerCase().includes(q);
  });

  if (!store) return null;

  return (
    <div>
      <div className="page-header">
        <h1>{dt('orders', lang)}</h1>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <select className="form-input" style={{ width: 'auto' }} value={filterStatus} onChange={(e) => { setFilterStatus(e.target.value); setPage(1); }}>
            <option value="">{dt('all_orders', lang)}</option>
            {statuses.map((s) => <option key={s} value={s}>{dt(s, lang)}</option>)}
          </select>
          {can('create') && <button className="btn btn-primary" onClick={openCreateModal}>
            <FiPlus style={{ marginInlineEnd: 4 }} /> {dt('create_order', lang)}
          </button>}
        </div>
      </div>

      {loading && page === 1 ? (
        <div className="empty-state"><p>{dt('loading', lang)}...</p></div>
      ) : orders.length === 0 ? (
        <div className="empty-state">
          <FiShoppingCart />
          <h3>{dt('no_orders', lang)}</h3>
          <p>{dt('orders_appear_here', lang)}</p>
        </div>
      ) : (
        <>
          <div className="data-table">
            <table>
              <thead>
                <tr>
                  <th>{dt('order_number', lang)}</th>
                  <th>{dt('customer', lang)}</th>
                  <th>{dt('phone', lang)}</th>
                  <th>{dt('total', lang)}</th>
                  <th>{dt('payment_status', lang)}</th>
                  <th>{dt('status', lang)}</th>
                  <th>{dt('date', lang)}</th>
                  <th>{dt('actions', lang)}</th>
                </tr>
              </thead>
              <tbody>
                {orders.map((order) => (
                  <tr key={order.id}>
                    <td><strong>{order.order_number}</strong></td>
                    <td>
                      {order.customer_id ? (
                        <button
                          onClick={() => navigate(`/dashboard/customers?id=${order.customer_id}`)}
                          style={{ background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600, color: 'var(--primary)', fontFamily: 'inherit', fontSize: 'inherit', padding: 0, textDecoration: 'underline' }}
                        >
                          {order.customer_name}
                        </button>
                      ) : order.customer_name}
                    </td>
                    <td>{order.customer_phone}</td>
                    <td><strong>{store.currency_symbol} {Number(order.total).toFixed(3)}</strong></td>
                    <td>
                      <span className={`badge ${order.payment_status === 'paid' ? 'badge-delivered' : 'badge-pending'}`}>
                        {order.payment_status === 'paid' ? dt('paid', lang) : dt('unpaid', lang)}
                      </span>
                    </td>
                    <td>
                      {can('edit') || can('cancel') ? <select
                        className="form-input"
                        value={order.status}
                        onChange={(e) => updateStatus(order.id, e.target.value)}
                        style={{ padding: '6px 10px', fontSize: 12, width: 'auto' }}
                      >
                        {statuses.filter((s) => s !== 'cancelled' || can('cancel') || order.status === 'cancelled').map((s) => (
                          <option key={s} value={s} disabled={s !== 'cancelled' && !can('edit')}>{dt(s, lang)}</option>
                        ))}
                      </select> : <span className={`badge badge-${order.status}`}>{dt(order.status, lang)}</span>}
                    </td>
                    <td>{new Date(order.created_at).toLocaleDateString(lang === 'ar' ? 'ar-KW' : 'en-US')}</td>
                    <td style={{ display: 'flex', gap: 4 }}>
                      <button className="btn btn-sm btn-white" onClick={() => setSelectedOrder(order)}>{dt('view', lang)}</button>
                      {can('cancel') && order.status !== 'cancelled' && <button className="btn btn-sm btn-white" onClick={() => runOrderAction(order, 'cancel')} style={{ color: '#d97706' }} title={lang === 'ar' ? 'إلغاء الطلب' : 'Cancel order'}><FiSlash size={14} /></button>}
                      {can('refund') && order.payment_status === 'paid' && <button className="btn btn-sm btn-white" onClick={() => runOrderAction(order, 'refund')} style={{ color: 'var(--danger)' }} title={lang === 'ar' ? 'استرجاع المبلغ' : 'Refund'}><FiRotateCcw size={14} /></button>}
                      {can('delete') && <button className="btn btn-sm btn-white" onClick={() => handleDeleteOrder(order.id)} style={{ color: 'var(--danger)' }} title={lang === 'ar' ? 'حذف' : 'Delete'}><FiTrash2 size={14} /></button>}
                      {order.payment_link_token && order.payment_status !== 'paid' && (
                        <button className="btn btn-sm btn-white" title={dt('payment_link', lang)} onClick={() => {
                          const base = store.custom_domain ? `https://${store.custom_domain}` : `https://${store.slug}.cartz.cc`;
                          setPaymentLink(`${base}/pay/${order.payment_link_token}`);
                          setShowLinkModal(true);
                        }}>
                          <FiLink size={14} />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {page < lastPage && (
            <div style={{ textAlign: 'center', marginTop: 16 }}>
              <button className="btn btn-white" onClick={() => setPage(page + 1)} disabled={loading}>
                {loading ? '...' : dt('load_more', lang)}
              </button>
            </div>
          )}
        </>
      )}

      {/* View Order Modal */}
      {selectedOrder && (
        <div className="modal-overlay" onClick={() => setSelectedOrder(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h2>{dt('order', lang)} {selectedOrder.order_number}</h2>
            {store?.platform_source === 'hadaq' && selectedOrder.type === 'delivery' && (
              <RidersOrderPanel
                order={selectedOrder}
                storeId={store.id}
                lang={lang}
                onUpdated={(updated: any) => {
                  setSelectedOrder((cur: any) => ({ ...cur, ...updated }));
                  setOrders((prev) => prev.map((o) => o.id === updated.id ? { ...o, ...updated } : o));
                }}
              />
            )}
            <div style={{ marginBottom: 20 }}>
              <p><strong>{dt('customer', lang)}:</strong>{' '}
                {selectedOrder.customer_id ? (
                  <button
                    onClick={() => { setSelectedOrder(null); navigate(`/dashboard/customers?id=${selectedOrder.customer_id}`); }}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600, color: 'var(--primary)', fontFamily: 'inherit', fontSize: 'inherit', padding: 0, textDecoration: 'underline' }}
                  >
                    {selectedOrder.customer_name}
                  </button>
                ) : selectedOrder.customer_name}
              </p>
              <p><strong>{dt('phone', lang)}:</strong> {selectedOrder.customer_phone}</p>
              {selectedOrder.customer_email && <p><strong>{dt('email', lang)}:</strong> {selectedOrder.customer_email}</p>}
              <p><strong>{dt('type', lang)}:</strong> {selectedOrder.type === 'delivery' ? dt('delivery', lang) : selectedOrder.type}</p>
              {selectedOrder.delivery_address && <p><strong>{dt('address', lang)}:</strong> {selectedOrder.delivery_address}</p>}
              {selectedOrder.delivery_address_details && (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: '6px 16px', padding: 12, marginBottom: 12, borderRadius: 8, background: 'var(--gray-50)', fontSize: 13 }}>
                  {Object.entries({
                    [lang === 'ar' ? 'المنطقة' : 'Area']: selectedOrder.delivery_address_details.area,
                    [lang === 'ar' ? 'القطعة' : 'Block']: selectedOrder.delivery_address_details.block,
                    [lang === 'ar' ? 'الشارع' : 'Street']: selectedOrder.delivery_address_details.street,
                    [lang === 'ar' ? 'المبنى' : 'Building']: selectedOrder.delivery_address_details.building,
                    [lang === 'ar' ? 'الطابق' : 'Floor']: selectedOrder.delivery_address_details.floor,
                    [lang === 'ar' ? 'الشقة' : 'Apartment']: selectedOrder.delivery_address_details.apartment,
                    [lang === 'ar' ? 'ملاحظات العنوان' : 'Address notes']: selectedOrder.delivery_address_details.notes,
                  }).filter(([, value]) => value).map(([label, value]) => (
                    <div key={label}><strong>{label}:</strong> {String(value)}</div>
                  ))}
                </div>
              )}
              {selectedOrder.notes && <p><strong>{dt('notes', lang)}:</strong> {selectedOrder.notes}</p>}
              <p><strong>{dt('payment_status', lang)}:</strong>{' '}
                <span className={`badge ${selectedOrder.payment_status === 'paid' ? 'badge-delivered' : 'badge-pending'}`}>
                  {selectedOrder.payment_status === 'paid' ? dt('paid', lang) : dt('unpaid', lang)}
                </span>
              </p>
            </div>
            {selectedOrder.items && (
              <div className="data-table" style={{ marginBottom: 20 }}>
                <table>
                  <thead>
                    <tr><th>{dt('item', lang)}</th><th>{dt('qty', lang)}</th><th>{dt('price', lang)}</th><th>{dt('total', lang)}</th></tr>
                  </thead>
                  <tbody>
                    {selectedOrder.items.map((item: any) => (
                      <tr key={item.id}>
                        <td>{item.product_name}</td>
                        <td>{item.quantity}</td>
                        <td>{store.currency_symbol} {Number(item.price).toFixed(3)}</td>
                        <td>{store.currency_symbol} {Number(item.total).toFixed(3)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <div style={{ textAlign: lang === 'ar' ? 'left' : 'right' }}>
              <p>{dt('subtotal', lang)}: {store.currency_symbol} {Number(selectedOrder.subtotal).toFixed(3)}</p>
              <p>{dt('delivery', lang)}: {store.currency_symbol} {Number(selectedOrder.delivery_fee).toFixed(3)}</p>
              <p style={{ fontSize: 18, fontWeight: 700 }}>{dt('total', lang)}: {store.currency_symbol} {Number(selectedOrder.total).toFixed(3)}</p>
            </div>
            <div className="modal-actions">
              {can('cancel') && selectedOrder.status !== 'cancelled' && (
                <button className="btn btn-white" style={{ color: '#d97706' }} onClick={() => runOrderAction(selectedOrder, 'cancel')}><FiSlash /> {lang === 'ar' ? 'إلغاء الطلب' : 'Cancel order'}</button>
              )}
              {can('refund') && selectedOrder.payment_status === 'paid' && (
                <button className="btn btn-white" style={{ color: 'var(--danger)' }} onClick={() => runOrderAction(selectedOrder, 'refund')}><FiRotateCcw /> {lang === 'ar' ? 'استرجاع المبلغ' : 'Refund'}</button>
              )}
              <button className="btn btn-white" onClick={() => setSelectedOrder(null)}>{dt('close', lang)}</button>
            </div>
          </div>
        </div>
      )}

      {/* Create Order Modal */}
      {showCreate && (
        <div className="modal-overlay" onClick={() => setShowCreate(false)}>
          <div className="modal" style={{ maxWidth: 600 }} onClick={(e) => e.stopPropagation()}>
            <h2>{dt('create_order', lang)}</h2>

            {/* Customer Mode Toggle */}
            <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
              <button
                type="button"
                className={`btn btn-sm ${customerMode === 'existing' ? 'btn-primary' : 'btn-white'}`}
                onClick={() => { setCustomerMode('existing'); setCustomerName(''); setCustomerPhone(''); setCustomerEmail(''); setSelectedCustomer(null); setCustomerSearch(''); }}
              >
                {lang === 'ar' ? 'عميل مسجل' : 'Existing Customer'}
              </button>
              <button
                type="button"
                className={`btn btn-sm ${customerMode === 'new' ? 'btn-primary' : 'btn-white'}`}
                onClick={() => { setCustomerMode('new'); setSelectedCustomer(null); setCustomerSearch(''); setCustomerName(''); setCustomerPhone(''); setCustomerEmail(''); }}
              >
                {lang === 'ar' ? 'عميل جديد' : 'New Customer'}
              </button>
            </div>

            {/* Customer Info */}
            {customerMode === 'existing' ? (
              <div style={{ marginBottom: 16, position: 'relative' }}>
                <label className="form-label">{lang === 'ar' ? 'بحث عن عميل' : 'Search Customer'}</label>
                <input
                  className="form-input"
                  value={customerSearch}
                  onChange={(e) => handleCustomerSearchChange(e.target.value)}
                  placeholder={lang === 'ar' ? 'ابحث بالاسم أو الهاتف أو البريد...' : 'Search by name, phone, or email...'}
                />
                {customers.length > 0 && !selectedCustomer && (
                  <div style={{
                    position: 'absolute', zIndex: 10, background: '#fff', border: '1px solid #e5e7eb',
                    borderRadius: 8, maxHeight: 200, overflowY: 'auto', width: '100%', boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                  }}>
                    {customers.map((c: any) => (
                      <div
                        key={c.id}
                        onClick={() => selectCustomer(c)}
                        style={{ padding: '10px 12px', cursor: 'pointer', borderBottom: '1px solid #f3f4f6' }}
                        onMouseEnter={(e) => (e.currentTarget.style.background = '#f9fafb')}
                        onMouseLeave={(e) => (e.currentTarget.style.background = '#fff')}
                      >
                        <div style={{ fontWeight: 500 }}>{c.name}</div>
                        <div style={{ fontSize: 12, color: '#6b7280' }}>{c.phone} {c.email ? `• ${c.email}` : ''}</div>
                      </div>
                    ))}
                  </div>
                )}
                {selectedCustomer && (
                  <div style={{ marginTop: 8, padding: '10px 14px', background: '#f0f9ff', borderRadius: 8, border: '1px solid #bae6fd', fontSize: 13 }}>
                    <strong>{selectedCustomer.name}</strong> — {selectedCustomer.phone} {selectedCustomer.email ? `• ${selectedCustomer.email}` : ''}
                  </div>
                )}
              </div>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
                <div>
                  <label className="form-label">{dt('customer', lang)} *</label>
                  <input className="form-input" value={customerName} onChange={(e) => setCustomerName(e.target.value)} placeholder={dt('customer', lang)} />
                </div>
                <div>
                  <label className="form-label">{dt('phone', lang)} *</label>
                  <input className="form-input" dir="ltr" value={customerPhone} onChange={(e) => setCustomerPhone(e.target.value)} placeholder={dt('phone', lang)} />
                </div>
                <div>
                  <label className="form-label">{dt('email', lang)}</label>
                  <input className="form-input" dir="ltr" value={customerEmail} onChange={(e) => setCustomerEmail(e.target.value)} placeholder={dt('email', lang)} />
                </div>
              </div>
            )}

            <div style={{ marginBottom: 16 }}>
              <label className="form-label">{dt('notes', lang)}</label>
              <input className="form-input" value={orderNotes} onChange={(e) => setOrderNotes(e.target.value)} placeholder={dt('notes', lang)} />
            </div>

            {/* Product Search */}
            <div style={{ marginBottom: 12, position: 'relative' }}>
              <label className="form-label">{dt('add_item', lang)}</label>
              <input
                className="form-input"
                value={productSearch}
                onChange={(e) => setProductSearch(e.target.value)}
                placeholder={dt('search_products', lang)}
              />
              {filteredProducts.length > 0 && (
                <div style={{
                  position: 'absolute', zIndex: 10, background: '#fff', border: '1px solid #e5e7eb',
                  borderRadius: 8, maxHeight: 200, overflowY: 'auto', width: '100%', boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                }}>
                  {filteredProducts.slice(0, 10).map((p: any) => (
                    <div
                      key={p.id}
                      onClick={() => addItem(p)}
                      style={{ padding: '8px 12px', cursor: 'pointer', borderBottom: '1px solid #f3f4f6', display: 'flex', justifyContent: 'space-between' }}
                      onMouseEnter={(e) => (e.currentTarget.style.background = '#f9fafb')}
                      onMouseLeave={(e) => (e.currentTarget.style.background = '#fff')}
                    >
                      <span>{p.name} {p.name_ar ? `- ${p.name_ar}` : ''}</span>
                      <span style={{ color: '#6b7280', fontSize: 13 }}>{store.currency_symbol} {Number(p.effective_price || p.price).toFixed(3)}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Items List */}
            {orderItems.length > 0 && (
              <div className="data-table" style={{ marginBottom: 16 }}>
                <table>
                  <thead>
                    <tr><th>{dt('item', lang)}</th><th>{dt('price', lang)}</th><th>{dt('qty', lang)}</th><th>{dt('total', lang)}</th><th></th></tr>
                  </thead>
                  <tbody>
                    {orderItems.map((i) => {
                      const unitPrice = i.product.effective_price || i.product.price;
                      return (
                        <tr key={i.product.id}>
                          <td>{i.product.name}</td>
                          <td>{store.currency_symbol} {Number(unitPrice).toFixed(3)}</td>
                          <td>
                            <input
                              type="number"
                              min={1}
                              value={i.quantity}
                              onChange={(e) => updateQty(i.product.id, parseInt(e.target.value) || 1)}
                              style={{ width: 60, padding: '4px 8px', borderRadius: 6, border: '1px solid #d1d5db', textAlign: 'center' }}
                            />
                          </td>
                          <td>{store.currency_symbol} {(unitPrice * i.quantity).toFixed(3)}</td>
                          <td>
                            <button onClick={() => removeItem(i.product.id)} style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer' }}>
                              <FiTrash2 size={16} />
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            <div style={{ textAlign: lang === 'ar' ? 'left' : 'right', marginBottom: 16, fontSize: 18, fontWeight: 700 }}>
              {dt('total', lang)}: {store.currency_symbol} {orderTotal.toFixed(3)}
            </div>

            <div className="modal-actions">
              <button className="btn btn-white" onClick={() => setShowCreate(false)}>{dt('cancel', lang)}</button>
              <button className="btn btn-primary" onClick={handleCreateOrder} disabled={creating || orderItems.length === 0}>
                {creating ? '...' : dt('create_order', lang)}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Payment Link Modal */}
      {showLinkModal && (
        <div className="modal-overlay" onClick={() => setShowLinkModal(false)}>
          <div className="modal" style={{ maxWidth: 500, textAlign: 'center' }} onClick={(e) => e.stopPropagation()}>
            <FiLink size={32} color="#4f46e5" style={{ marginBottom: 12 }} />
            <h2>{dt('payment_link', lang)}</h2>
            <p style={{ color: '#6b7280', fontSize: 14, marginBottom: 16 }}>
              {lang === 'ar' ? 'شارك هذا الرابط مع العميل للدفع' : 'Share this link with the customer to pay'}
            </p>
            <div style={{
              background: '#f3f4f6', borderRadius: 8, padding: '12px 16px',
              display: 'flex', alignItems: 'center', gap: 8, direction: 'ltr', marginBottom: 16,
              wordBreak: 'break-all', fontSize: 13,
            }}>
              <span style={{ flex: 1, textAlign: 'left' }}>{paymentLink}</span>
              <button onClick={copyLink} className="btn btn-primary btn-sm" style={{ whiteSpace: 'nowrap' }}>
                <FiCopy size={14} style={{ marginInlineEnd: 4 }} /> {dt('copy_link', lang)}
              </button>
            </div>
            <div className="modal-actions">
              <button className="btn btn-white" onClick={() => setShowLinkModal(false)}>{dt('close', lang)}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Courier panel on an order: dispatch to Riders, follow the driver, cancel.
 * Only rendered for Hadaq stores on delivery orders.
 */
function RidersOrderPanel({ order, storeId, lang, onUpdated }: any) {
  const [busy, setBusy] = useState(false);
  const t = (en: string, ar: string) => (lang === 'ar' ? ar : en);

  const call = async (action: 'dispatch' | 'cancel' | 'refresh') => {
    setBusy(true);
    try {
      const res = await api.post(`/stores/${storeId}/riders/orders/${order.id}/${action}`);
      const updated = res.data?.order || res.data;
      if (res.data?.message) toast.success(res.data.message);
      onUpdated(updated);
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Error');
    } finally {
      setBusy(false);
    }
  };

  const dispatched = !!order.riders_uid;

  return (
    <div style={{
      border: '1px solid var(--gray-100)', borderRadius: 8, padding: 12,
      marginBottom: 16, background: 'var(--gray-50)',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: dispatched ? 10 : 0 }}>
        <FiTruck size={15} color="var(--gray-500)" />
        <strong style={{ fontSize: 13, flex: 1 }}>{t('Riders delivery', 'توصيل رايدرز')}</strong>
        {dispatched && (
          <span style={{
            fontSize: 11, fontWeight: 700, padding: '3px 9px', borderRadius: 999,
            background: 'rgba(37,99,235,0.1)', color: '#2563eb', fontFamily: 'monospace',
          }}>
            {order.riders_fleet_status || '—'}
          </span>
        )}
      </div>

      {dispatched ? (
        <div style={{ fontSize: 13, display: 'grid', gap: 4 }}>
          <div><strong>{t('Reference', 'المرجع')}:</strong> {order.riders_order_number || order.riders_uid}</div>
          {order.riders_driver_name && (
            <div>
              <strong>{t('Driver', 'المندوب')}:</strong> {order.riders_driver_name}
              {order.riders_driver_phone && (
                <a href={`tel:${order.riders_driver_phone}`} style={{ marginInlineStart: 8 }}>{order.riders_driver_phone}</a>
              )}
            </div>
          )}
          {order.riders_cost != null && (
            <div><strong>{t('Courier cost', 'تكلفة التوصيل')}:</strong> {Number(order.riders_cost).toFixed(3)}</div>
          )}
          <div style={{ display: 'flex', gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
            {order.riders_tracking_url && (
              <a className="btn btn-white btn-sm" href={order.riders_tracking_url} target="_blank" rel="noopener noreferrer">
                {t('Track', 'تتبع')}
              </a>
            )}
            <button className="btn btn-white btn-sm" onClick={() => call('refresh')} disabled={busy}>
              {t('Refresh', 'تحديث')}
            </button>
            {order.riders_is_cancellable && (
              <button className="btn btn-white btn-sm" style={{ color: 'var(--danger)' }}
                onClick={() => call('cancel')} disabled={busy}>
                {t('Cancel delivery', 'إلغاء التوصيل')}
              </button>
            )}
          </div>
        </div>
      ) : (
        <div style={{ marginTop: 8 }}>
          <button className="btn btn-primary btn-sm" onClick={() => call('dispatch')} disabled={busy}>
            {busy ? t('Sending…', 'جاري الإرسال…') : t('Send to Riders', 'إرسال إلى رايدرز')}
          </button>
          {order.riders_last_error && (
            <div style={{ fontSize: 12, color: 'var(--danger)', marginTop: 8 }}>{order.riders_last_error}</div>
          )}
        </div>
      )}
    </div>
  );
}
